import { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { t } from 'i18next'
import { message } from 'antd'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

import { AppModal, ShareSelectInput } from '@components'
import { Button, Text } from 'src/common'
import { TTeacherPackage } from '@interfaces'
import { TeacherPackageManagementApi } from 'src/api/teacher-package-management'
import { deleteTeacherPackageSchema } from 'src/pages/package-management/teacher-package-management/config'

interface IProps {
  open: boolean
  onClose: () => void
  currentPackageId: number
  onConfirmDelete: (selectPackageId: number) => void
}

const DeletePackageModal = ({
  onClose,
  open,
  currentPackageId,
  onConfirmDelete,
}: IProps) => {
  const [listpackages, setListpackages] = useState<TTeacherPackage[]>([])
  const [isLoadingPackages, setIsLoadingPackages] = useState(false)

  const {
    reset,
    handleSubmit,
    getValues,
    control,
    formState: { isValid },
    clearErrors,
  } = useForm({
    defaultValues: { selectPackageId: 0 },
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(deleteTeacherPackageSchema),
  })

  const handleOnCloseModal = useCallback(() => {
    setListpackages([])
    reset()
    onClose()
  }, [onClose, reset])

  const handleOnSubmit = handleSubmit((value) => {})

  const getAllListPackages = async () => {
    try {
      setIsLoadingPackages(true)
      const res = await TeacherPackageManagementApi.getListTeacherPackages({
        limit: 9999,
        page: 1,
      })
      setListpackages(res.data.teacherPackages.items)
    } catch (error) {
      message.error(t('error:error_connect_server'))
    } finally {
      setIsLoadingPackages(false)
    }
  }

  const teacherPackageOptions = useMemo(
    () =>
      listpackages
        .filter((item) => item.id !== currentPackageId && item.isActive)
        .map((it) => ({ label: it.name, value: it.id })),
    [currentPackageId, listpackages]
  )

  const modalContent = useMemo(
    () => (
      <ModalContentWrapper onSubmit={handleOnSubmit}>
        <Text className="!text-[16px]">
          {t('teacher:confirm_delete_package')}
        </Text>
        <Controller
          name="selectPackageId"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <ShareSelectInput
              data={teacherPackageOptions}
              onChange={(value) => onChange(Number(value))}
              value={value > 0 ? value : undefined}
              placeholder={
                isLoadingPackages
                  ? (t('teacher:getting_data') as string)
                  : (t(
                      'teacher:select_another_package_before_delete'
                    ) as string)
              }
              errors={error?.message}
              className="w-max min-w-[30dvw]"
              disabled={isLoadingPackages}
              showArrow
            />
          )}
        />
        <ButtonWrapper>
          <Button
            type="default"
            size="small"
            id="blog-form"
            onClick={handleOnCloseModal}
          >
            {t('common:cancel')}
          </Button>
          <Button
            htmlType="submit"
            type="primary"
            size="small"
            id="blog-form"
            onClick={() => {
              if (!isValid) return
              onConfirmDelete(getValues('selectPackageId'))
              handleOnCloseModal()
            }}
            loading={isLoadingPackages}
          >
            {t('common:confirm')}
          </Button>
        </ButtonWrapper>
      </ModalContentWrapper>
    ),
    [
      control,
      handleOnCloseModal,
      handleOnSubmit,
      isLoadingPackages,
      teacherPackageOptions,
      isValid,
      onConfirmDelete,
      getValues,
    ]
  )

  useEffect(() => {
    if (open) {
      clearErrors()
      getAllListPackages()
    }
  }, [clearErrors, open])

  return (
    <AppModal
      open={open}
      onClose={handleOnCloseModal}
      title={t('teacher:delete_package')}
      haveCloseIcon
    >
      {modalContent}
    </AppModal>
  )
}

export default DeletePackageModal

const ModalContentWrapper = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  align-items: center;
`
